import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { X, Users, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PendingInvitationBanner = () => {
  const { user } = useAuth();
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (user) {
      checkPendingInvitation();
    }
  }, [user]);

  const checkPendingInvitation = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/team/pending-invitation');
      if (response.data.has_invitation) {
        setInvitation(response.data.invitation);
        // Check if user already dismissed this invitation
        const dismissedInvites = JSON.parse(localStorage.getItem('dismissedInvitations') || '[]');
        if (dismissedInvites.includes(response.data.invitation.invitation_id)) {
          setDismissed(true);
        }
      }
    } catch (error) {
      // No pending invitation or error - that's fine
      console.log('No pending invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!invitation) return;
    
    setAccepting(true);
    try {
      await api.post('/api/team/accept-invitation', {
        invitation_id: invitation.invitation_id
      });
      toast.success('You have joined the team! Enjoy your Enterprise access.');
      setInvitation(null);
      // Redirect to account page to see team info
      window.location.href = '/account';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  const handleDismiss = () => {
    if (invitation) {
      // Store dismissed invitation ID
      const dismissedInvites = JSON.parse(localStorage.getItem('dismissedInvitations') || '[]');
      dismissedInvites.push(invitation.invitation_id);
      localStorage.setItem('dismissedInvitations', JSON.stringify(dismissedInvites));
    }
    setDismissed(true);
  };

  // Don't show if loading, no invitation, or dismissed
  if (loading || !invitation || dismissed) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 py-3 relative">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">
              <span className="font-bold">{invitation.inviter_name || invitation.inviter_email}</span> invited you to join their Enterprise team!
            </p>
            <p className="text-xs text-indigo-200 hidden sm:block">
              Accept to get shared access to 1M rows/month and all premium features.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="bg-white text-indigo-600 hover:bg-indigo-50 h-8 px-4 text-sm font-medium"
            data-testid="accept-invitation-btn"
          >
            {accepting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Accept
                <ArrowRight className="w-3 h-3 ml-1" />
              </>
            )}
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingInvitationBanner;
